import { motion } from 'framer-motion';
import { Scale, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';

export default function ComplianceStatement() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="bg-charcoal text-charcoal-foreground py-20">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-primary text-sm mb-4">
              <Link to={ROUTE_PATHS.HOME} className="hover:text-primary/80 transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span>Compliance Statement</span>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Scale className="text-primary" size={24} />
              </div>
              <span className="text-primary text-xs font-semibold tracking-widest uppercase">Legal Document III / III</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Compliance Statement</h1>
            <p className="text-charcoal-foreground/70 text-lg">
              SHONCHIN MANOR &amp; CO. — Our commitment to integrity, legality, and responsible business conduct.
            </p>
            <p className="text-charcoal-foreground/50 text-sm mt-3">Effective date: 2026 · © 2026 SHONCHIN MANOR &amp; CO.</p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="bg-card border border-border rounded-2xl p-8 mb-8 text-sm text-muted-foreground leading-relaxed">
            SHONCHIN MANOR &amp; CO. regards integrity and lawful conduct as the foundation of its practice. This Statement sets out the principal compliance principles we observe in providing strategic and management consulting services, and our formal undertakings to clients, partners and all other stakeholders.
          </div>

          <Section number="1" title="Our Commitment">
            <p>We commit to complying, in all business activities, with applicable laws, regulations and internationally recognised standards; to maintaining professional integrity; and to preventing misconduct in any form. Compliance is the shared responsibility of every member of the firm.</p>
          </Section>

          <Section number="2" title="Anti-Bribery and Anti-Corruption">
            <p>The Company strictly prohibits its directors, employees, agents and affiliates from offering, giving, accepting or soliciting bribes, kickbacks, improper benefits or any unlawful transfer of value, whether to or from a public official, a commercial counterparty or any other person.</p>
            <p>Business hospitality, gifts, travel sponsorship and charitable donations are subject to prior approval and contemporaneous written records, to ensure that they are lawful, reasonable and transparent.</p>
            <LegalRef>UAE Federal Decree-Law No. 31 of 2021 on the Issuance of the Crimes and Penalties Law (anti-bribery provisions); the U.S. Foreign Corrupt Practices Act (15 U.S.C. §§ 78dd-1 et seq.); the U.K. Bribery Act 2010; and the United Nations Convention against Corruption.</LegalRef>
          </Section>

          <Section number="3" title="Anti-Money Laundering & Counter-Terrorist Financing">
            <p>The Company refuses to participate in, or facilitate, money laundering, terrorist financing or any related unlawful activity. All engagements accepted by us must arise from lawful sources and be for legitimate purposes.</p>
            <p>We conduct reasonable risk-based due diligence on clients, including identity verification and assessment of business background. Where suspicions arise, we reserve the right to decline an engagement and to discharge any applicable reporting obligation.</p>
            <LegalRef>UAE Federal Decree-Law No. 20 of 2018 on Anti-Money Laundering and Combating the Financing of Terrorism and Financing of Illegal Organisations (as amended by Federal Decree-Law No. 26 of 2021); and the Forty Recommendations of the Financial Action Task Force (FATF).</LegalRef>
          </Section>

          <Section number="4" title="Sanctions and Export Controls">
            <p>The Company complies with applicable international and economic sanctions, including the resolutions of the UN Security Council and the measures promulgated by the United Arab Emirates, the United States, the European Union and the United Kingdom. We will not enter into business relationships with sanctioned persons, regions or end-uses.</p>
            <p>Before accepting any cross-border engagement, we conduct reasonable sanctions screening of clients and counterparties, and where relevant alert clients to export-licensing requirements applicable to dual-use items and controlled technologies.</p>
          </Section>

          <Section number="5" title="Data Protection and Information Security">
            <p>The Company observes the data-protection and privacy laws applicable to its business, as further set out in our Privacy Policy. We maintain information-security measures commensurate with our scale of operations and continuously improve them by reference to internationally recognised standards.</p>
            <LegalRef>UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection (PDPL); the EU General Data Protection Regulation (GDPR); the California Consumer Privacy Act as amended by CPRA; and ISO/IEC 27001 on Information Security Management.</LegalRef>
          </Section>

          <Section number="6" title="Conflicts of Interest and Confidentiality">
            <p>The Company identifies, discloses and reasonably manages actual or potential conflicts of interest so that clients receive service that is impartial and independent. A conflicts check is performed before accepting each engagement.</p>
            <p>The Company and all its personnel are bound by strict professional confidentiality. We will not disclose or use a client's trade secrets or confidential information save with the client's written consent or as expressly required by law. Confidentiality obligations survive the end of any employment or engagement.</p>
          </Section>

          <Section number="7" title="Fair Competition and Professional Ethics">
            <p>We support free, fair and orderly market competition, and strictly prohibit false advertising, commercial disparagement, commercial bribery and misappropriation of third-party trade secrets. The Company does not participate in monopoly agreements or abuse of market dominance.</p>
            <p>We are committed to maintaining a professional environment that is diverse, equitable, and free from discrimination or harassment.</p>
          </Section>

          <Section number="8" title="Training and Culture">
            <p>The Company provides regular compliance training to all staff on anti-bribery, AML, data protection, information security, conflicts of interest and professional ethics. New joiners must complete foundational training and sign a Code of Conduct upon commencement.</p>
            <p>Compliance is a continuous process. We periodically review and refine our compliance policies, controls and training to align with the latest legal requirements and industry practice.</p>
          </Section>

          <Section number="9" title="Reporting and Non-Retaliation">
            <p>The Company encourages employees, clients, partners and other stakeholders to report any conduct suspected to be unlawful or contrary to this Statement. The Company undertakes that:</p>
            <ul>
              <li>the identity of the reporter and the contents of the report will be held in strict confidence;</li>
              <li>no retaliation of any kind will be taken against a good-faith reporter;</li>
              <li>each report will be investigated independently, objectively and promptly.</li>
            </ul>
          </Section>

          <div className="mt-8 p-4 bg-muted/40 border border-border rounded-xl text-xs text-muted-foreground italic">
            Disclaimer: This Statement does not constitute legal advice and does not create any contractual relationship between the Company and any third party. The Company reserves the right to amend this Statement at any time in response to legal, regulatory or business developments.
          </div>
        </motion.div>

        {/* Footer nav between legal docs */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row gap-4 justify-between">
          <Link to={ROUTE_PATHS.PRIVACY} className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium">
            <ChevronRight size={16} /> Privacy Policy
          </Link>
          <Link to={ROUTE_PATHS.TERMS} className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium">
            <ChevronRight size={16} /> Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
        <span className="w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center flex-shrink-0">
          {number}
        </span>
        {title}
      </h2>
      <div className="text-muted-foreground leading-relaxed space-y-3 pl-11">
        {children}
      </div>
    </div>
  );
}

function LegalRef({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 p-4 bg-primary/5 border-l-4 border-primary rounded-r-lg text-xs text-muted-foreground">
      <span className="font-semibold text-primary uppercase tracking-wide text-xs">Legal Reference — </span>
      {children}
    </div>
  );
}
